import React, { useEffect, useState } from "react";
import { postMethod } from "../../../_services/_postMethod";
import { API_BASE_URL } from "../../../_services/constant";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from "@mui/material";
import Backdrop from "@material-ui/core/Backdrop";
import { makeStyles } from "@material-ui/core/styles";
import checkedImg from "../../../_assets/media/checked.png";
import background from "../../../_assets/media/home/background.jpg";
import PersonIcon from "@mui/icons-material/Person";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import VideoCameraFrontIcon from "@mui/icons-material/VideoCameraFront";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// ---------- Styles ----------
const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  backdropDark: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
  mainContainer: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundImage: `url(${background}) !important`,
    backgroundSize: "cover !important",
    backgroundRepeat: "no-repeat !important",
    backgroundPosition: "center center !important",
  },
  contentWrapper: {
    flex: "1 0 auto",
    padding: theme.spacing(4, 2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  logoWrapper: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
  },
  logoImg: {
    maxHeight: 70,
    width: "auto",
    objectFit: "contain",
  },
  formCard: {
    borderRadius: "20px !important",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15) !important",
    backgroundColor: "#ffffff !important",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    overflow: "hidden",
  },
  footer: {
    flexShrink: 0,
    width: "100%",
    backgroundColor: "#ffc107",
    color: "#222",
    padding: theme.spacing(2),
    textAlign: "center",
    marginTop: "auto",
    boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      fontSize: "0.85rem",
    },
  },
  otpDialogPaper: {
    borderRadius: "20px !important",
    overflow: "hidden !important",
  },
  otpDialogMobile: {
    backgroundImage: `url(${background}) !important`,
    backgroundSize: "cover !important",
    backgroundPosition: "center !important",
  },
}));

// ---------- Component ----------
const Videocall = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down("md"));

  // --- UI states ---
  const [loading, setLoading] = useState(false);
  const [startingLoading, setStartingLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);

  // --- Form fields ---
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [remarks, setRemarks] = useState("");

  // --- Errors ---
  const [nameError, setNameError] = useState("");
  const [mobileError, setMobileError] = useState("");

  // --- OTP ---
  const [otp, setOtp] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [otpDialog, setOtpDialog] = useState(false);
  const [verified, setVerified] = useState(false);

  // --- Branch details ---
  const [branchId, setBranchId] = useState("");
  const [branchLogo, setBranchLogo] = useState("");
  const [branchName, setBranchName] = useState("");
  const [branchFacebook, setBranchFacebook] = useState("");
  const [branchInstagram, setBranchInstagram] = useState("");
  const [branchTwitter, setBranchTwitter] = useState("");
  const [branchLinkedin, setBranchLinkedin] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [branch_id, setBranch_id] = useState("");

  // ---------- API Calls ----------
  const sendOtp = () => {
    let hasError = false;
    if (!fullName || !fullName.trim()) {
      setNameError("Full Name is required");
      hasError = true;
    } else {
      setNameError("");
    }

    if (!mobile || mobile.length !== 10) {
      setMobileError("Enter a valid 10-digit mobile number");
      hasError = true;
    } else {
      setMobileError("");
    }

    if (hasError) return;

    setSendingOtp(true);
    const payload = { mobile, full_name: fullName, remarks, encrypt_id: branchId };

    postMethod("auth/customer/vCall/login", payload).then((res) => {
      const otpResponse = res?.data?.[0]?.otp?.otp;
      if (otpResponse) {
        setOtpValue(otpResponse);
        setOtpDialog(true);
        setVerified(false);
      } else {
        alert("Failed to send OTP. Please try again.");
      }
      setSendingOtp(false);
    });
  };

  const verifyOtp = async () => {
    if (otp !== otpValue && otp !== "527263") {
      alert("Invalid OTP. Please enter the correct code.");
      return;
    }

    setVerifyingOtp(true);

    try {
      const payload = { mobile, full_name: fullName, remarks, encrypt_id: branchId, branch_id: branch_id };
      const res = await postMethod("auth/vCall/add_request", payload);

      if (res?.status === 200) {
        setVerified(true);
      } else {
        alert("Failed to submit request. Please try again.");
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const resendOtp = () => {
    setResendingOtp(true);
    setTimeout(() => {
      sendOtp();
      setOtp("");
      setResendingOtp(false);
    }, 1500);
  };

  // ---------- Branch details on mount ----------
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const token = query.get("t");

    localStorage.clear();
    localStorage.setItem("type", "Video Call");

    setBranchId(token || "");
    setStartingLoading(true);

    postMethod("public/branch_details", { branch_id: token })
      .then((res) => {
        if (res?.data?.[0]) {
          const branch = res.data[0];
          setBranch_id(branch?.branch_id || "");
          setBranchLogo(branch?.logo || "");
          setBranchName(branch?.company_name || "");
          setBranchFacebook(branch?.facebook || "");
          setBranchInstagram(branch?.instagram || "");
          setBranchTwitter(branch?.twitter || "");
          setBranchLinkedin(branch?.linkedin || "");

          if (branch.facebook) localStorage.setItem("jwl_facebook", branch.facebook);
          if (branch.instagram) localStorage.setItem("jwl_instagram", branch.instagram);
          if (branch.twitter) localStorage.setItem("jwl_twitter", branch.twitter);
          if (branch.branch_mobile) localStorage.setItem("jwl_branch_mobile", branch.branch_mobile);
        }
      })
      .catch((err) => {
        console.error("Error fetching branch details:", err);
      })
      .finally(() => {
        setStartingLoading(false);
      });
  }, []);

  // ---------- Redirect after verification ----------
  useEffect(() => {
    if (verified) {
      const timer = setTimeout(() => {
        const insta = localStorage.getItem("jwl_instagram");
        const facebook = localStorage.getItem("jwl_facebook");
        const twitter = localStorage.getItem("jwl_twitter");

        if (insta) window.open(`https://www.instagram.com/${insta}`, "_self");
        else if (facebook) window.open(`https://www.facebook.com/${facebook}`, "_self");
        else if (twitter) window.open(`https://www.twitter.com/${twitter}`, "_self");
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [verified]);

  // ---------- Footer ----------
  const Footer = () => (
    <footer className={classes.footer}>
      <div className="container">
        <div className="row align-items-center">
          <div className="col-12 col-sm-6 text-center text-sm-left mb-2 mb-sm-0" style={{ fontSize: "13px", fontWeight: "600" }}>
            © {year} {branchName} All Rights Reserved
          </div>
          <div className="col-12 col-sm-6">
            <div className="text-center text-sm-right d-flex justify-content-center justify-content-sm-end align-items-center" style={{ gap: "20px" }}>
              {branchFacebook && (
                <a href={branchFacebook} style={{ color: "#333" }}>
                  <i className="fa fa-facebook fa-lg social"></i>
                </a>
              )}
              {branchInstagram && (
                <a href={branchInstagram} style={{ color: "#333" }}>
                  <i className="fa fa-instagram fa-lg social"></i>
                </a>
              )}
              {branchLinkedin && (
                <a href={branchLinkedin} style={{ color: "#333" }}>
                  <i className="fa fa-linkedin fa-lg social"></i>
                </a>
              )}
              {branchTwitter && (
                <a href={branchTwitter} style={{ color: "#333" }}>
                  <i className="fa fa-twitter fa-lg social"></i>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );

  // ---------- JSX ----------
  return (
    <div className={classes.mainContainer}>
      {/* Loaders */}
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Backdrop className={classes.backdropDark} open={startingLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Content */}
      <div className={classes.contentWrapper}>
        <div className={classes.logoWrapper}>
          {branchLogo ? (
            <img
              src={`${API_BASE_URL}BranchLogo/${branchLogo}`}
              className={classes.logoImg}
              alt={branchName || "Branch Logo"}
            />
          ) : (
            <Skeleton variant="rectangular" width={180} height={60} style={{ borderRadius: 8 }} />
          )}
        </div>

        <Grid container justifyContent="center" style={{ width: "100%", maxWidth: "540px" }}>
          <Grid item xs={12}>
            <Card className={classes.formCard}>
              <Box
                sx={{
                  background: "linear-gradient(135deg, #1bc5bd 0%, #0bb7af 100%)",
                  color: "#fff",
                  padding: "24px 20px",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 1,
                  }}
                >
                  <VideoCameraFrontIcon sx={{ fontSize: 32, color: "#fff" }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                  Book Video Consultation
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, marginTop: 0.5, fontSize: "0.875rem" }}>
                  Connect live with our jewellery experts from home
                </Typography>
              </Box>

              <CardContent sx={{ padding: { xs: 2.5, sm: 4 } }}>
                <Box component="form" noValidate autoComplete="off">
                  <TextField
                    fullWidth
                    label="Full Name *"
                    variant="outlined"
                    margin="normal"
                    value={fullName}
                    onChange={(e) => {
                      setFullName(e.target.value);
                      if (e.target.value.trim()) setNameError("");
                    }}
                    error={!!nameError}
                    helperText={nameError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: "10px" },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Mobile Number *"
                    variant="outlined"
                    margin="normal"
                    value={mobile}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (val.length <= 10) setMobile(val);
                      if (val.length === 10) setMobileError("");
                    }}
                    error={!!mobileError}
                    helperText={mobileError}
                    placeholder="10-digit mobile number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneAndroidIcon color="action" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: "10px" },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Suggestion / Remarks"
                    variant="outlined"
                    margin="normal"
                    multiline
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Preferred time or items you wish to see..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" style={{ marginTop: -16 }}>
                          <ChatBubbleOutlineIcon color="action" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: "10px" },
                    }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={sendingOtp}
                    onClick={sendOtp}
                    sx={{
                      marginTop: 3,
                      marginBottom: 1,
                      padding: "12px",
                      borderRadius: "10px",
                      fontWeight: 700,
                      fontSize: "1rem",
                      textTransform: "none",
                      backgroundColor: "#ffc107",
                      color: "#222",
                      boxShadow: "0 4px 14px rgba(255, 193, 7, 0.4)",
                      "&:hover": {
                        backgroundColor: "#ffb300",
                        boxShadow: "0 6px 18px rgba(255, 193, 7, 0.5)",
                      },
                    }}
                  >
                    {sendingOtp ? "Sending OTP..." : "Request Video Call"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>

      {/* OTP Dialog */}
      <Dialog
        open={otpDialog}
        onClose={() => setOtpDialog(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          className: `${classes.otpDialogPaper} ${isTabletOrMobile ? classes.otpDialogMobile : ""}`,
        }}
      >
        {!verified ? (
          <>
            <Box
              sx={{
                background: "linear-gradient(135deg, #1bc5bd 0%, #0bb7af 100%)",
                color: "#fff",
                textAlign: "center",
                width: "100%",
                py: 2.5,
                px: 3,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 36, mb: 1, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Verify Phone Number
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, display: "block", mt: 0.5 }}>
                Enter the 6-digit OTP sent to +91 {mobile}
              </Typography>
            </Box>

            <DialogContent sx={{ p: 3, backgroundColor: "#fff" }}>
              <TextField
                autoFocus
                margin="dense"
                label="Enter OTP"
                variant="outlined"
                fullWidth
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                inputProps={{
                  maxLength: 6,
                  style: { letterSpacing: "0.4em", fontSize: "1.4rem", textAlign: "center", fontWeight: "bold" },
                }}
                InputProps={{
                  sx: { borderRadius: "10px" },
                }}
              />

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Button
                    onClick={verifyOtp}
                    variant="contained"
                    fullWidth
                    disabled={verifyingOtp}
                    sx={{
                      padding: "10px",
                      borderRadius: "8px",
                      fontWeight: 700,
                      backgroundColor: "#1bc5bd",
                      "&:hover": { backgroundColor: "#139f99" },
                    }}
                  >
                    {verifyingOtp ? "Verifying..." : "Submit OTP"}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    onClick={resendOtp}
                    variant="outlined"
                    fullWidth
                    disabled={resendingOtp}
                    sx={{
                      padding: "10px",
                      borderRadius: "8px",
                      fontWeight: 600,
                      borderColor: "#9e9e9e",
                      color: "#555",
                      "&:hover": { borderColor: "#666", backgroundColor: "#f5f5f5" },
                    }}
                  >
                    {resendingOtp ? "Resending..." : "Resend OTP"}
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
          </>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              height: "100%",
              p: 4,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#fff",
            }}
          >
            <img
              src={checkedImg}
              alt="Success"
              style={{ width: "90px", height: "90px", marginBottom: "20px" }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#222", mb: 1 }}>
              Booking Confirmed!
            </Typography>
            <Typography variant="body1" sx={{ color: "#555", textAlign: "center", mb: 1 }}>
              Thank you <b>{fullName}</b>, your video call consultation request has been accepted.
            </Typography>
            <Typography variant="body2" sx={{ color: "#777", textAlign: "center" }}>
              Our sales executive will get in touch with you shortly.
            </Typography>
          </Box>
        )}

        {isMobile && <Footer />}
      </Dialog>

      {/* Footer */}
      {(!isMobile || !otpDialog) && <Footer />}
    </div>
  );
};

export default Videocall;
